import type { AiToolkitCollection, AiToolkitCollectionItem } from "@mem-client/ai-toolkit/contract";
import type { AppStore } from "@/store/AppStore";
import type { CollectionItemObservable } from "@/store/collection-items/CollectionItemObservable";
import type { CollectionObservable } from "@/store/collections/CollectionObservable";
import type { INoteObservable } from "@/store/note";
import {
  buildLocalNote,
  DEFAULT_PREVIEW_CHAR_LIMIT,
} from "@/modules/ai-toolkit/tools/notes/note-tools.helpers";

type CollectionSource = Pick<
  CollectionObservable,
  | "createdAt"
  | "description"
  | "id"
  | "isFavorited"
  | "isShared"
  | "itemList"
  | "label"
  | "lastAddedToAt"
  | "lastInteractedAt"
  | "lastViewedAt"
  | "modifiedAt"
  | "path"
  | "sharedAt"
  | "title"
>;

type CollectionItemSource = Pick<
  CollectionItemObservable,
  "collectionId" | "id" | "itemId" | "itemKind" | "modelData"
>;

const emptyStringToNull = (value: string | null | undefined): string | null => value || null;

const isAvailableNote = (note: INoteObservable | null | undefined): note is INoteObservable =>
  !!note?.isAvailable;

export const buildCollection = async ({
  collection,
}: {
  collection: CollectionSource;
}): Promise<AiToolkitCollection> => {
  const itemIds = await collection.itemList.getAllItemIdsAsync();

  return {
    id: collection.id,
    title: collection.title ?? null,
    label: collection.label,
    description: collection.description ?? null,
    path: collection.path,
    isShared: collection.isShared,
    isFavorited: collection.isFavorited,
    itemCount: itemIds.length,
    createdAt: collection.createdAt,
    modifiedAt: collection.modifiedAt,
    lastViewedAt: emptyStringToNull(collection.lastViewedAt),
    lastAddedToAt: emptyStringToNull(collection.lastAddedToAt),
    lastInteractedAt: emptyStringToNull(collection.lastInteractedAt),
    sharedAt: emptyStringToNull(collection.sharedAt),
  };
};

export const buildCollectionItem = async ({
  store,
  collectionItem,
  includeNotePreview = false,
  maxContentChars = DEFAULT_PREVIEW_CHAR_LIMIT,
}: {
  store: AppStore;
  collectionItem: CollectionItemSource;
  includeNotePreview?: boolean;
  maxContentChars?: number;
}): Promise<AiToolkitCollectionItem> => {
  const note =
    collectionItem.itemKind === "NOTE"
      ? await store.notes.getAsync(collectionItem.itemId)
      : undefined;

  return {
    id: collectionItem.id,
    collectionId: collectionItem.collectionId,
    itemId: collectionItem.itemId,
    itemKind: collectionItem.itemKind,
    addedAt: collectionItem.modelData.locally_created_at,
    addedBySpaceAccountId: collectionItem.modelData.added_by_space_account_id ?? null,
    note: isAvailableNote(note)
      ? await buildLocalNote({
          store,
          note,
          includeContent: includeNotePreview ? "plaintext" : "none",
          maxContentChars,
          missingContentBehavior: "return_status",
        })
      : null,
  };
};
